
/**
 * custom-variant-pills.js
 *
 * Click handler for `<button class="custom-variant-pill">` elements
 * rendered by `snippets/custom-variant-pills.liquid`.
 *
 * Two responsibilities (both delegated via document-level listeners so
 * they cover cards inserted via XHR/innerHTML after page load):
 *
 *   A) Pill click  → update the card's visual selection, hidden form
 *                    input, link href, displayed price, and image.
 *   B) Add-to-Cart → if a pill was selected, intercept the click and
 *                    POST directly to /cart/add.js (bypasses theme/app
 *                    form interception that overrides the variant).
 *
 * Loaded from `sections/product-recommendations.liquid` in the section's
 * initial server-rendered HTML so the listeners are bound BEFORE the
 * <product-recommendations> web component fetches and innerHTML-injects
 * the card markup (innerHTML-injected <script> tags don't execute).
 */
(() => {
  // Idempotency guard — if loaded twice, second run no-ops.
  if (window.__customVariantPillsBound) return;
  window.__customVariantPillsBound = true;

  /** Set to false to silence console logs in production. */
  const DEBUG = true;

  /** Centralized CSS selectors — change once, applied everywhere. */
  const S = {
    pill: '.custom-variant-pill',
    pillMore: 'custom-variant-pill--more',
    pillSelected: 'custom-variant-pill--selected',
    card: 'product-card, .product-card',
    variantInput: 'input[name="id"]',
    qtyInput: 'input[name="quantity"]',
    cardLink: '.product-card__link',
    cardImage:
      '.product-card__image img, .card-gallery img, .product-media img, img.product-card__image',
    /**
     * Price text element(s) to overwrite when a variant is selected.
     *
     * Horizon renders the regular price as:
     *   <div class="price__regular">
     *     <span class="price">$123.00</span>     ← target
     *   </div>
     * And the sale price as:
     *   <span class="price-item--sale price">$99.00</span>   ← target
     *   <span class="price-item--regular compare-at-price">$123.00</span>   ← do NOT overwrite (strikethrough)
     *
     * We deliberately avoid `.compare-at-price` because that's the original
     * (struck-through) price next to a sale price.
     */
    price:
      'product-price .price__regular .price, product-price .price-item--sale, .price__regular > .price, .price-item--sale',
    addBtn: 'add-to-cart-component button[type="submit"]',
    addBtnWrapper:
      'add-to-cart-component, product-form-component, .buy-buttons-block, [data-block-type="buy-buttons"], [data-block-type="add-to-cart"]',
    notifyBtn: '.custom-notify-btn',
    notifyWrapper: '.custom-notify',
    cartDrawer: 'cart-drawer-component, cart-drawer',
    cartSections:
      'cart-items-component[data-section-id], cart-drawer-component[data-section-id], cart-drawer[data-section-id]',
    cartCount: '[data-cart-count], .cart-count, .cart-icon__count, cart-count',
  };

  const log = (...args) => DEBUG && console.log('[variant-pills]', ...args);

  /** Shopify routing — respects multi-locale `routes.root`. */
  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_ADD_URL = `${ROOT}cart/add.js`;
  const CART_GET_URL = `${ROOT}cart.js`;

  /* ============================================================
   * Because recommendations render on a PDP, Klaviyo's BIS adapter would
   * read the PDP product's context. We temporarily SPOOF the Shopify
   * product context (ShopifyAnalytics.meta, window.meta, a shadow
   * /cart/add form, and the ?variant= URL param) to the recommended
   * product/variant, open the form, then restore the original context
   * on close/submit. On submit we also call Klaviyo's Client BIS API
   * directly so the subscription is guaranteed for the right variant.
   * ============================================================ */
  const KLAVIYO_BIS_FORM_ID = 'UuSfSN';
  const KLAVIYO_PUBLIC_API_KEY = 'WdLqxR';

  const kbis = {
    pendingContext: null,
    originalShopifyMeta: null,
    originalMeta: null,
    originalUrl: null,
    contextActive: false,
    handleOverride: '', // product handle Klaviyo's .js fetch should be rewritten to
  };

  const isNumericId = (v) => /^\d+$/.test(String(v || '').trim());
  const buildShopifyVariantId = (variantId) => '$shopify:::$default:::' + String(variantId).trim();

  /**
   * Create/refresh the hidden /cart/add shim form Klaviyo's BIS adapter
   * reads `input[name="id"]` from. Mirrors the glossary engine.
   */
  const installVariantShim = (variantId, productId) => {
    if (!variantId || !isNumericId(variantId)) return;
    let shim = document.getElementById('custom-bis-shadow-form');
    if (!shim) {
      shim = document.createElement('form');
      shim.id = 'custom-bis-shadow-form';
      shim.action = '/cart/add';
      shim.method = 'post';
      shim.setAttribute(
        'style',
        'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;'
      );
      shim.innerHTML =
        '<input type="hidden" name="id"><input type="hidden" name="product-id">';
      document.body.appendChild(shim);
    }
    shim.querySelector('input[name="id"]').value = String(variantId);
    const pidInput = shim.querySelector('input[name="product-id"]');
    if (pidInput) pidInput.value = productId ? String(productId) : '';
  };

  /**
   * Spoof Shopify product context to the REAL recommended product/variant.
   * Unlike the previous synthetic approach, this injects the actual product
   * object returned by /products/<handle>.js and points the URL at the real
   * product path — exactly what the working glossary engine does, so Klaviyo's
   * BIS adapter resolves the correct catalog variant.
   *
   * @param {object} product  - Real product object from /products/<handle>.js.
   * @param {string} variantId - Validated variant id.
   * @param {string} handle    - Product handle (for the spoofed URL).
   */
  const applyShopifyContext = (product, variantId, handle) => {
    if (!product || !variantId) return;

    if (!kbis.contextActive) {
      kbis.originalShopifyMeta = window.ShopifyAnalytics?.meta ?? null;
      kbis.originalMeta = window.meta || null;
      kbis.originalUrl = { pathname: location.pathname, search: location.search || '' };
      kbis.contextActive = true;
    }

    try {
      window.meta = Object.assign({}, window.meta || {}, {
        product,
        selectedVariantId: Number(variantId),
      });
    } catch (e) {}

    try {
      window.ShopifyAnalytics = window.ShopifyAnalytics || {};
      window.ShopifyAnalytics.meta = Object.assign({}, window.ShopifyAnalytics.meta || {}, {
        product: { id: product.id, gid: product.id, type: 'product', variants: product.variants },
        selectedVariantId: Number(variantId),
        currency: window.Shopify?.currency?.active || 'USD',
        page: { pageType: 'product', resourceType: 'product', resourceId: product.id },
      });
    } catch (e) {}

    // Point the URL at the REAL product path so a raw `<url>.js` fetch resolves.
    try {
      if (handle) {
        const root = window.Shopify?.routes?.root || '/';
        const url =
          root.replace(/\/?$/, '/') +
          'products/' +
          encodeURIComponent(handle) +
          '?variant=' +
          encodeURIComponent(String(variantId));
        history.replaceState({ customBISSpoof: true }, '', url);
      }
    } catch (e) {}

    installVariantShim(variantId, product.id);
  };

  const restoreShopifyContext = () => {
    if (!kbis.contextActive) return;

    if (kbis.originalShopifyMeta === null) {
      if (window.ShopifyAnalytics) delete window.ShopifyAnalytics.meta;
    } else if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.meta = kbis.originalShopifyMeta;
    }

    if (kbis.originalMeta === null) {
      try { delete window.meta; } catch (e) { window.meta = undefined; }
    } else {
      window.meta = kbis.originalMeta;
    }

    if (kbis.originalUrl) {
      try {
        history.replaceState({}, '', kbis.originalUrl.pathname + kbis.originalUrl.search);
      } catch (e) {}
    }

    document.getElementById('custom-bis-shadow-form')?.remove();

    kbis.contextActive = false;
    kbis.originalShopifyMeta = null;
    kbis.originalMeta = null;
    kbis.originalUrl = null;
    kbis.handleOverride = '';
  };

  // Guard so we don't fire duplicate subscriptions for the same email+variant
  // (native klaviyoForms submit + direct capture can both trigger).
  let lastSubscribeKey = '';

  const subscribeBackInStock = (email, phone, context) => {
    if (!context || !context.variantId || !KLAVIYO_PUBLIC_API_KEY) {
      console.warn('[variant-pills] BIS subscribe skipped: missing context/variant/apiKey', {
        hasContext: !!context,
        variantId: context && context.variantId,
        hasApiKey: !!KLAVIYO_PUBLIC_API_KEY,
      });
      return;
    }
    if (!isNumericId(context.variantId)) {
      console.warn('[variant-pills] BIS subscribe skipped: variant id not numeric', context.variantId);
      return;
    }
    if (!email && !phone) {
      console.warn('[variant-pills] BIS subscribe skipped: no email/phone');
      return;
    }

    const dedupeKey = `${email || phone}::${context.variantId}`;
    if (dedupeKey === lastSubscribeKey) {
      // log('BIS subscribe deduped', dedupeKey);
      return;
    }
    lastSubscribeKey = dedupeKey;
    setTimeout(() => {
      if (lastSubscribeKey === dedupeKey) lastSubscribeKey = '';
    }, 8000);

    const channels = [];
    if (email) channels.push('EMAIL');
    if (phone) channels.push('SMS');

    const profileAttributes = {};
    if (email) profileAttributes.email = email;
    if (phone) profileAttributes.phone_number = phone;

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: profileAttributes } },
          channels,
        },
        relationships: {
          variant: { data: { type: 'catalog-variant', id: buildShopifyVariantId(context.variantId) } },
        },
      },
    };



    fetch(
      'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' +
        encodeURIComponent(KLAVIYO_PUBLIC_API_KEY),
      {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', revision: '2024-06-15' },
        body: JSON.stringify(payload),
      }
    )
      .then((res) => {
        if (res.ok || res.status === 202) {
          console.log('[variant-pills] ✅ BIS subscription created', {
            status: res.status,
            variantId: context.variantId,
          });
          return;
        }
        return res.text().then((txt) => {
          console.error('[variant-pills] ❌ BIS subscription FAILED', res.status, txt);
        });
      })
      .catch((err) => console.error('[variant-pills] ❌ BIS subscribe network error:', err));
  };

  /* ============================================================
   * Rewrite Klaviyo's OWN Back-in-Stock subscription POST.
   *
   * On a PDP, Klaviyo's BIS form binds to the CURRENT PDP product, so its
   * native subscription request goes out with the PDP variant — creating the
   * event for the wrong product. While a recommendation BIS is in progress
   * (kbis.pendingContext set), we rewrite the catalog-variant id in the POST
   * body to OUR resolved recommended variant. This is the airtight fix because
   * it doesn't depend on what context Klaviyo computed — we correct the request
   * itself, right before it leaves the browser.
   *
   * @param {string} body - The JSON request body Klaviyo is about to send.
   * @returns {string|null} Patched body string, or null if no change.
   */
  const BIS_ENDPOINT_RE = /\/client\/back-in-stock-subscriptions\//i;

  const patchBisSubscriptionBody = (body) => {
    if (!kbis.pendingContext || typeof body !== 'string') return null;
    if (!isNumericId(kbis.pendingContext.variantId)) return null;
    try {
      const json = JSON.parse(body);
      const variantData = json?.data?.relationships?.variant?.data;
      if (!variantData || !variantData.id) return null;
      const desired = buildShopifyVariantId(kbis.pendingContext.variantId);
      if (variantData.id === desired) return null;
      const original = variantData.id;
      variantData.id = desired;
      return JSON.stringify(json);
    } catch (e) {
      return null;
    }
  };

  /* ============================================================
   * Klaviyo product-JS fetch rewrite.
   *
   * Klaviyo's BIS adapter (getProductVariantData) fetches the CURRENT
   * page URL + ".js" to read variant data (e.g. /pages/glossary/creatine.js
   * → 404, or on a PDP it fetches the WRONG product's .js). We rewrite those
   * requests to the correct /products/<handle>.js while a BIS context is open.
   * Ported from the CLP `snizeEnsureKlaviyoFetchRewrite`.
   * ============================================================ */
  const buildProductJsUrl = (handle) => {
    if (!handle) return '';
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '.js';
  };

  /* ============================================================
   * Real product fetch + variant validation (from the glossary engine).
   * Guarantees the variant id we subscribe with actually exists on the
   * recommended product, preventing Klaviyo's "Product Variant does not
   * exist" error and ensuring the restock email is tied to the right variant.
   * ============================================================ */
  const productCache = Object.create(null);

  const fetchProduct = (handle, cb) => {
    if (!handle) return cb(null);
    if (productCache[handle] !== undefined) return cb(productCache[handle]);
    fetch(buildProductJsUrl(handle), { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        productCache[handle] = p || null;
        cb(productCache[handle]);
      })
      .catch(() => {
        productCache[handle] = null;
        cb(null);
      });
  };

  const resolveVariantFromProduct = (product, preferred) => {
    if (!product || !product.variants || !product.variants.length) return '';
    const pref = String(preferred || '').trim();
    if (pref && product.variants.some((v) => String(v.id) === pref)) return pref;
    const soldOut = product.variants.find((v) => !v.available);
    return String((soldOut || product.variants[0]).id);
  };

  const fixKlaviyoProductJsUrl = (reqUrl) => {
    const handle = kbis.handleOverride;
    if (!handle || !reqUrl) return reqUrl;
    const urlStr = String(reqUrl);

    let needsRewrite =
      /\/collections\/[^?#]+\.js(?:\?|#|$)/i.test(urlStr) ||
      /\/pages\/[^?#]+\.js(?:\?|#|$)/i.test(urlStr);

    // On a PDP, Klaviyo fetches /products/<pdp-handle>.js — rewrite it unless
    // it's already the product we want.
    if (!needsRewrite) {
      const m = urlStr.match(/\/products\/([^/?#]+)\.js(?:\?|#|$)/i);
      if (m && decodeURIComponent(m[1]) !== handle) needsRewrite = true;
    }

    // Also catch "<current pathname>.js" (covers /pages/ and /collections/).
    if (!needsRewrite) {
      const path = window.location && window.location.pathname ? window.location.pathname : '';
      if (path) {
        const suffix = path + '.js';
        if (urlStr === suffix || urlStr.indexOf(suffix) !== -1) needsRewrite = true;
      }
    }

    if (!needsRewrite) return reqUrl;
    const productJsPath = buildProductJsUrl(handle);
    if (!productJsPath) return reqUrl;
    if (/^https?:\/\//i.test(urlStr)) {
      const origin = window.location && window.location.origin ? window.location.origin : '';
      return origin ? origin + productJsPath : productJsPath;
    }
    return productJsPath;
  };

  const ensureKlaviyoFetchRewrite = () => {
    if (window.__customKlaviyoFetchRewrite) return;
    window.__customKlaviyoFetchRewrite = true;

    const nativeFetch = window.fetch;
    if (nativeFetch) {
      window.fetch = function (input, init) {
        try {
          const reqUrl = typeof input === 'string' ? input : input && input.url ? input.url : '';

          // Correct Klaviyo's own BIS subscription to OUR recommended variant.
          if (init && init.body && BIS_ENDPOINT_RE.test(reqUrl)) {
            const patched = patchBisSubscriptionBody(init.body);
            if (patched) init = Object.assign({}, init, { body: patched });
          }

          const fixed = fixKlaviyoProductJsUrl(reqUrl);
          if (fixed && fixed !== reqUrl) return nativeFetch.call(this, fixed, init);
        } catch (e) {}
        return nativeFetch.call(this, input, init);
      };
    }

    if (window.XMLHttpRequest && XMLHttpRequest.prototype.open) {
      const nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url, async, user, password) {
        try {
          this.__customBisUrl = typeof url === 'string' ? url : '';
          if (typeof url === 'string') url = fixKlaviyoProductJsUrl(url);
        } catch (e) {}
        return nativeOpen.call(this, method, url, async, user, password);
      };

      const nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function (body) {
        try {
          if (body && this.__customBisUrl && BIS_ENDPOINT_RE.test(this.__customBisUrl)) {
            const patched = patchBisSubscriptionBody(body);
            if (patched) body = patched;
          }
        } catch (e) {}
        return nativeSend.call(this, body);
      };
    }
  };
  ensureKlaviyoFetchRewrite();

  /**
   * Prime Klaviyo's onsite session with the viewed product/variant.
   * This is the piece the CLP version has that makes restock emails fire:
   * it seeds Klaviyo's active-on-site tracking so the BIS subscription is
   * tied to the correct catalog variant.
   */
  const primeKlaviyoProductContext = (variantId, productId, handle) => {
    if (!variantId) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = handle
        ? root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '?variant=' + variantId
        : window.location.href;
      const item = {
        Title: '',
        ItemId: productId ? String(productId) : String(variantId),
        Url: url,
        Metadata: { VariantId: String(variantId) },
      };
      if (window.klaviyo && typeof window.klaviyo.trackViewedItem === 'function') {
        window.klaviyo.trackViewedItem(item);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['trackViewedItem', item]);
      }
    } catch (e) {}
  };

  const openFormWithRetries = (formId, attempts) => {
    attempts = attempts || 0;
    try {
      if (window._klOnsite && typeof window._klOnsite.openForm === 'function') {
        window._klOnsite.openForm(formId);
        return;
      }
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId]);
      return;
    } catch (e) {}
    if (attempts >= 15) {
      console.warn('[variant-pills] Could not open Klaviyo BIS form:', formId);
      return;
    }
    setTimeout(() => openFormWithRetries(formId, attempts + 1), 200);
  };

  /**
   * Open the Klaviyo BIS form for a specific recommended product/variant,
   * spoofing the Shopify context first so Klaviyo binds the right variant.
   * @param {HTMLElement} notifyContainer - The `.custom-notify` wrapper.
   */
  const openKlaviyoBIS = (notifyContainer) => {
    const preferredVariantId = notifyContainer.querySelector(S.notifyBtn)?.dataset.variantId;
    const productId = notifyContainer.dataset.productId;
    if (!preferredVariantId || !productId) return;

    const handle = notifyContainer.dataset.productHandle || '';

    // Tell the fetch-rewrite which product's .js Klaviyo should read.
    kbis.handleOverride = handle || '';

    const proceed = (product, variantId) => {
      if (!variantId || !isNumericId(variantId)) {
        // log('BIS: could not resolve a numeric variant id', { preferredVariantId, handle });
        return;
      }

      kbis.pendingContext = {
        formId: KLAVIYO_BIS_FORM_ID,
        productId,
        productHandle: handle,
        productTitle: notifyContainer.dataset.productTitle,
        productUrl: notifyContainer.dataset.productUrl,
        variantId,
        ts: Date.now(),
      };

      // Spoof real product context when we have the product; otherwise at least
      // install the variant shim so Klaviyo can read the variant id.
      if (product) applyShopifyContext(product, variantId, handle);
      else installVariantShim(variantId, productId);

      // Prime Klaviyo onsite tracking (the key piece that makes emails fire).
      primeKlaviyoProductContext(variantId, productId, handle);

      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push([
          'track',
          'Viewed Back In Stock Form',
          {
            ProductID: productId,
            ProductName: notifyContainer.dataset.productTitle,
            VariantID: variantId,
            URL: notifyContainer.dataset.productUrl,
          },
        ]);
      }

      setTimeout(() => openFormWithRetries(KLAVIYO_BIS_FORM_ID), 80);
    };

    // Fetch the real product, validate the variant, then open.
    if (handle) {
      fetchProduct(handle, (product) => {
        if (product) {
          const vid = resolveVariantFromProduct(product, preferredVariantId);
          proceed(product, vid || preferredVariantId);
        } else {
          proceed(null, preferredVariantId);
        }
      });
    } else {
      proceed(null, preferredVariantId);
    }
  };

  // Klaviyo form lifecycle: subscribe on submit, restore context on close/submit.
  window.addEventListener('klaviyoForms', (e) => {
    const detail = e?.detail;
    if (!detail || !kbis.pendingContext) return;
    if (detail.formId && detail.formId !== kbis.pendingContext.formId) return;
    if (Date.now() - kbis.pendingContext.ts > 15 * 60 * 1000) {
      kbis.pendingContext = null;
      restoreShopifyContext();
      return;
    }

    const type = detail.type;
    if (type === 'submit' || type === 'stepSubmit') {
      const md = detail.metaData || {};
      const email = md.email || md.$email || '';
      const phone = md.phone_number || md.$phone_number || '';
      if (email || phone) subscribeBackInStock(email, phone, kbis.pendingContext);
    }

    if (type === 'close') {
      // Closed without submitting → tear down immediately.
      restoreShopifyContext();
      kbis.pendingContext = null;
    } else if (type === 'submit' || type === 'redirectedToUrl') {
      // Keep the context alive briefly so the body-rewrite still applies to
      // Klaviyo's own subscription POST (which can fire slightly AFTER this
      // lifecycle event), then tear down.
      setTimeout(() => {
        restoreShopifyContext();
        kbis.pendingContext = null;
      }, 2500);
    }
  });

  window.addEventListener('pagehide', restoreShopifyContext);

  /* ============================================================
   * DIRECT SUBMIT-CAPTURE (the key reliability piece from the CLP version)
   *
   * Rather than relying solely on Klaviyo's native form submission (which
   * can bind to the PDP product's context), we intercept the submit on the
   * Klaviyo BIS form, read the email from the DOM, and POST the Client BIS
   * subscription ourselves with OUR validated variant. This guarantees the
   * subscription — and therefore the restock email — is for the right variant.
   * ============================================================ */
  const KLAVIYO_FORM_SEL =
    'motion.form-component, [data-testid="klaviyo-form"], div[class*="kl-private"], div[class*="klaviyo"], form[class*="klaviyo"], div[role="dialog"][class*="kl"], div[class*="needsclick"], iframe[src*="klaviyo"]';

  const isElementInKlaviyoForm = (el) => !!(el && el.closest && el.closest(KLAVIYO_FORM_SEL));

  const EMAIL_INPUT_SEL =
    'input[type="email"], input[name="email"], input[autocomplete="email"], input[placeholder*="email" i]';

  const findKlaviyoFormEmail = () => {
    // Pass 1: prefer an email input that's clearly inside a Klaviyo form.
    const inputs = document.querySelectorAll(EMAIL_INPUT_SEL);
    for (let i = inputs.length - 1; i >= 0; i--) {
      if (!isElementInKlaviyoForm(inputs[i])) continue;
      const v = String(inputs[i].value || '').trim();
      if (v && VALID_EMAIL.test(v)) return v;
    }
    // Pass 2 (fallback): a BIS context is open, so any filled, valid email
    // input on the page is almost certainly the Klaviyo form's. This rescues
    // cases where Klaviyo's container markup doesn't match our selectors.
    for (let i = inputs.length - 1; i >= 0; i--) {
      const v = String(inputs[i].value || '').trim();
      if (v && VALID_EMAIL.test(v)) return v;
    }
    return '';
  };

  const isKlaviyoBisSubmitControl = (el) => {
    if (!el) return false;
    if (el.type === 'submit') return true;
    const txt = String(el.textContent || el.value || el.getAttribute('aria-label') || '')
      .trim()
      .toLowerCase();
    return (
      txt.indexOf('notify') !== -1 ||
      txt.indexOf('submit') !== -1 ||
      txt.indexOf('sign up') !== -1 ||
      txt.indexOf('send') !== -1 ||
      txt.indexOf('subscribe') !== -1
    );
  };

  const VALID_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const captureBisSubmit = (reason) => {
    if (!kbis.pendingContext) return;
    if (Date.now() - kbis.pendingContext.ts > 15 * 60 * 1000) {
      kbis.pendingContext = null;
      restoreShopifyContext();
      return;
    }
    const email = findKlaviyoFormEmail();
    if (!email || !VALID_EMAIL.test(email)) {
      log('capture: no valid email found yet', { reason });
      return;
    }
    // log('capture: subscribing', { reason, email });
    subscribeBackInStock(email, '', kbis.pendingContext);
  };

  document.addEventListener(
    'click',
    (ev) => {
      if (!kbis.pendingContext) return;
      const control = ev.target.closest && ev.target.closest('button, [role="button"], input[type="submit"]');
      if (!control || !isElementInKlaviyoForm(control)) return;
      // Try immediately, and again shortly after (Klaviyo may populate/validate
      // the field on click). Permissive: any click on a control inside the
      // Klaviyo form attempts a capture — captureBisSubmit no-ops without email.
      captureBisSubmit('click');
      setTimeout(() => captureBisSubmit('click+timeout'), 250);
    },
    true
  );

  document.addEventListener(
    'submit',
    (ev) => {
      if (!kbis.pendingContext) return;
      if (!ev.target || !isElementInKlaviyoForm(ev.target)) return;
      captureBisSubmit('submit');
    },
    true
  );

  /** Format cents as money, using Shopify's formatter when available. */
  const formatMoney = (cents) => {
    const fmt = window.Shopify?.formatMoney;
    return typeof fmt === 'function'
      ? fmt(cents, window.Shopify.currency?.format)
      : `$${(cents / 100).toFixed(2)}`;
  };

  // log('script loaded');

  /**
   * Apply a pill's variant state to its parent card.
   * Updates: form input, card link, selected style, price, image, add-to-cart state.
   * Shared by Listener A (user clicks pill) and initCards (auto-sync on load).
   *
   * @param {HTMLElement} pill - The pill button being applied.
   * @param {HTMLElement} card - The product-card the pill belongs to.
   * @param {boolean}     fromUserClick - When true, also swap selected class on siblings.
   */
  const applyPillState = (pill, card, fromUserClick) => {
    const { variantId, variantUrl, variantImage, variantPrice, variantAvailable } = pill.dataset;
    if (!variantId) return;

    // Persist on the card for Listener B to read on cart-add.
    card.dataset.customSelectedVariantId = variantId;

    // 1. Hidden form input (no events fired — apps interpret them as variant resets)
    const input = card.querySelector(S.variantInput);
    if (input) {
      input.value = variantId;
      input.setAttribute('value', variantId);
    }

    // 2. Whole-card link
    const cardLink = card.querySelector(S.cardLink);
    if (cardLink && variantUrl) cardLink.href = variantUrl;

    // 3. Visual "selected" state — only swap when triggered by a real user click.
    //    On init, the server already rendered the correct pill as selected.
    if (fromUserClick) {
      card.querySelectorAll(`.${S.pillSelected}`).forEach((p) => {
        p.classList.remove(S.pillSelected);
        p.setAttribute('aria-pressed', 'false');
      });
      pill.classList.add(S.pillSelected);
      pill.setAttribute('aria-pressed', 'true');
    }

    // 4. Price text — overwrite every matching price element in this card.
    //    Skips compare-at-price (strikethrough) so sale-price strikethrough stays intact.
    const cents = parseInt(variantPrice, 10);
    if (!isNaN(cents)) {
      const formatted = formatMoney(cents);
      const priceEls = card.querySelectorAll(S.price);
      if (priceEls.length === 0) {
        // log('no price element found in card (selector miss?)', card);
      }
      priceEls.forEach((el) => {
        if (el.classList.contains('compare-at-price')) return; // don't kill the strikethrough
        el.textContent = formatted;
      });
    }

    // 5. Card image — only swap if the new image differs from the current src.
    if (variantImage) {
      const img = card.querySelector(S.cardImage);
      if (img && !img.src.endsWith(variantImage.split('?')[0])) {
        img.src = variantImage;
        img.removeAttribute('srcset');
      }
    }

    // 6. Availability: swap between Add-to-cart and Notify-me.
    toggleCardAvailability(card, variantAvailable === 'false', variantId);
  };

  /**
   * Show exactly ONE of {Add-to-cart, Notify-me} for a card.
   *
   * When the selected variant is unavailable → hide the buy-buttons wrapper
   * and show the notify button (and vice versa). Extracted so it can run for
   * cards WITHOUT variant pills too (single-variant / "Default Title"
   * products), where the server already rendered the notify button visible
   * but nothing was hiding the buy-buttons block — which is why both showed.
   *
   * @param {HTMLElement} card
   * @param {boolean} isUnavailable
   * @param {string} [variantId] - keep the notify button pointed at this variant.
   */
  const toggleCardAvailability = (card, isUnavailable, variantId) => {
    const notifyBtn = card.querySelector(S.notifyBtn);

    // Collect every add-to-cart wrapper in this card. Use a Set because the
    // fallback below can rediscover an element already matched by the selector.
    const wrappers = new Set();
    card.querySelectorAll(S.addBtnWrapper).forEach((el) => wrappers.add(el));

    // Fallback: some themes/blocks wrap the add button in a plain <span> with
    // no distinctive class. Walk up from any real add/submit button (never the
    // notify button) to a sensible wrapper so we can still hide it.
    card.querySelectorAll('button[name="add"], button[type="submit"]').forEach((btn) => {
      if (btn.closest(S.notifyWrapper) || btn.closest(S.notifyBtn)) return;
      const wrapper =
        btn.closest('add-to-cart-component, product-form-component, .buy-buttons-block') ||
        btn.parentElement ||
        btn;
      wrappers.add(wrapper);
    });

    // Only hide buy buttons if we actually have a notify button to show in their
    // place; otherwise leave the theme's default behaviour intact.
    const hide = isUnavailable && !!notifyBtn;
    wrappers.forEach((w) => {
      w.style.display = hide ? 'none' : '';
      w.querySelectorAll?.('button').forEach((b) => {
        if (!b.closest(S.notifyWrapper)) b.disabled = isUnavailable;
      });
    });

    if (notifyBtn) {
      if (variantId) notifyBtn.dataset.variantId = variantId;
      notifyBtn.classList.toggle('custom-notify-btn--hidden', !isUnavailable);
    }

    // log('availability toggled', {
    //   isUnavailable,
    //   hid: hide,
    //   wrappersFound: wrappers.size,
    // });
  };

  /**
   * Sync every product card so its image/price/form match its server-side
   * pre-selected pill. Runs once on load and whenever new cards are added
   * to the DOM (e.g. <product-recommendations> XHR injection).
   *
   * Guarded by `card.dataset.customInitialized` so the same card isn't
   * synced twice (which would needlessly fight the user's selection).
   */
  const initCards = (root) => {
    (root || document).querySelectorAll(S.card).forEach((card) => {
      if (card.dataset.customInitialized) return;
      card.dataset.customInitialized = '1';

      const pill = card.querySelector(`.${S.pillSelected}`);
      if (pill) {
        // Card has pills → sync image/price/form/availability to the
        // server-selected pill (applyPillState handles availability too).
        applyPillState(pill, card, false);
        // log('card initialized to pre-selected pill', { variantId: pill.dataset.variantId });
        return;
      }

      // No selected pill (single-variant / "Default Title" / overflowed
      // selection). Still reconcile Add-to-cart vs Notify-me from the
      // notify button's server-rendered state so we never show BOTH.
      const notifyBtn = card.querySelector(S.notifyBtn);
      if (!notifyBtn) return;
      const isUnavailable = !notifyBtn.classList.contains('custom-notify-btn--hidden');
      toggleCardAvailability(card, isUnavailable, notifyBtn.dataset.variantId);
      // log('card availability synced (no pills)', { isUnavailable });
    });
  };

  // Initial pass for any cards already in the DOM at script load.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCards());
  } else {
    initCards();
  }

  // Watch for cards added later (recommendations / quick-view / paginated loads).
  // Using a single observer on body with subtree:true is cheap because the
  // callback bails fast when no `.product-card` is in the added nodes.
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.matches?.(S.card) || node.querySelector?.(S.card)) {
          initCards(node.parentNode || document);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // ============================================================
  // Listener A — PILL CLICK
  // ============================================================
  let lastPillPointerDownTs = 0;
  const selectPill = (pill, card, event) => {
    // This interaction belongs to the pill — don't let the card overlay/link
    // or the view-transition handler act on it.
    event.preventDefault();
    event.stopPropagation();
    applyPillState(pill, card, true);
    // log('pill selected', { type: event.type, variantId: pill.dataset.variantId });
  };
  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

    const pill = event.target.closest(S.pill);
    if (!pill || pill.classList.contains(S.pillMore)) return;

    const card = pill.closest(S.card);
    if (!card) return;

    // applyPillState(pill, card, true);
    // log('pill clicked', { variantId: pill.dataset.variantId });
    lastPillPointerDownTs = Date.now();
      selectPill(pill, card, event);
    },
    true /* capture phase */
  );
  document.addEventListener(
    'click',
    (event) => {
      const pill = event.target.closest(S.pill);

      if (pill && !pill.classList.contains(S.pillMore)) {
        const card = pill.closest(S.card);
        if (card) selectPill(pill, card, event);
        return;
      }

      // The click didn't land on a pill, but a pill pointerdown fired moments
      // ago on this card — the pointer drifted onto the card overlay while the
      // card was animating. Swallow this stray click so it doesn't navigate.
      // Never swallow a real interactive control (add-to-cart, notify, etc.);
      // only cancel drift onto the non-interactive card link overlay / media.
      const interactive = event.target.closest('button, input, label, select, [role="button"]');
      if (
        !interactive &&
        Date.now() - lastPillPointerDownTs < 500 &&
        event.target.closest(S.card)
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true /* capture phase */
  );

  // document.addEventListener('click', (event) => {
  //   const pill = event.target.closest(S.pill);
  //   if (!pill || pill.classList.contains(S.pillMore)) return;

  //   const card = pill.closest(S.card);
  //   if (!card) return;

  //   // applyPillState(pill, card, true);
  //   // log('pill clicked', { variantId: pill.dataset.variantId });
  //   // This click belongs to the pill — don't let the card overlay/link or
  //     // the view-transition handler act on it.
  //     event.preventDefault();
  //     event.stopPropagation();

  //     applyPillState(pill, card, true);
  //     log('pill clicked', { variantId: pill.dataset.variantId });
  //   },
  //   true /* capture phase */
  // );

  // ============================================================
  // Listener: NOTIFY-ME (Back in Stock) click
  // ============================================================
  document.addEventListener('click', (event) => {
    const notifyBtn = event.target.closest(S.notifyBtn);
    if (!notifyBtn) return;

    event.preventDefault();
    const notifyContainer = notifyBtn.closest('.custom-notify') || notifyBtn.parentElement;
    // log('notify-me clicked', { variantId: notifyBtn.dataset.variantId });
    if (notifyContainer) openKlaviyoBIS(notifyContainer);
  });

  // ============================================================
  // Listener B — ADD-TO-CART INTERCEPT (direct /cart/add.js fetch)
  // Runs in capture phase + stops propagation so theme/app handlers
  // can't override the variant before the POST fires.
  // ============================================================

   const isOnCartPage = () =>
    [...document.querySelectorAll('cart-items-component')].some(
      (el) => !el.closest('cart-drawer-component, #recharge-storefront-experiences__cart-drawer')
    );

  /**
   * Scroll the cart page to the top so the updated bag is in view.
   * Desktop Horizon scrolls `.page-wrapper`, not the window.
   */
  const scrollCartPageToTop = () => {
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    const wrapper = desktop ? document.querySelector('.page-wrapper') : null;
    if (wrapper) {
      wrapper.scrollTo({ top: 0, behavior });
    } else {
      window.scrollTo({ top: 0, behavior });
    }
  };

  /**
   * Ask cart-items-component to morph from section HTML without opening the
   * drawer. `action: 'add'` is what cart-drawer uses to auto-open, so this
   * refresh uses a different action.
   * @param {{ sections?: Record<string, string>, itemCount?: number, items?: unknown[] }} payload
   */
  const refreshCartInPlace = (payload) => {
    let resolveUpdate = () => {};
    const linesUpdate = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    linesUpdate.promise = new Promise((resolve) => {
      resolveUpdate = resolve;
    });
    linesUpdate.action = 'update';
    linesUpdate.context = 'product';
    linesUpdate.lines = [];
    document.dispatchEvent(linesUpdate);
    resolveUpdate({
      cart: {
        totalQuantity: payload.itemCount ?? 0,
      },
      detail: {
        sections: payload.sections,
        items: payload.items,
        itemCount: payload.itemCount,
        didError: false,
        source: 'custom-variant-pills',
      },
    });
  };

  /**
   * Return an UNPATCHED fetch. ReCharge Storefront Experiences monkey-patches
   * window.fetch and auto-opens its cart drawer whenever it sees a /cart/add
   * request. On the cart page we want an in-place update with NO drawer (and no
   * open/close flash), so we route that one add through a pristine fetch grabbed
   * from a hidden blank same-origin iframe — ReCharge never wrapped that one.
   * The iframe is kept in the DOM so its contentWindow (and fetch) stays alive.
   */
  let __unpatchedFetch = null;
  const getUnpatchedFetch = () => {
    if (__unpatchedFetch) return __unpatchedFetch;
    try {
      const ifr = document.createElement('iframe');
      ifr.setAttribute('aria-hidden', 'true');
      ifr.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
      (document.body || document.documentElement).appendChild(ifr);
      const f = ifr.contentWindow && ifr.contentWindow.fetch;
      if (f) __unpatchedFetch = f.bind(ifr.contentWindow);
    } catch (e) {}
    if (!__unpatchedFetch) __unpatchedFetch = window.fetch.bind(window);
    return __unpatchedFetch;
  };

  document.addEventListener(
    'click',
    async (event) => {
      const addBtn = event.target.closest(S.addBtn);
      if (!addBtn) return;

      const card = addBtn.closest(S.card);
      const fromCartRecommendations =
        isOnCartPage() && !!addBtn.closest('product-recommendations');
      // Pill selection wins. On the cart page, fall back to the form's variant
      // so every recommendation card is intercepted — otherwise the theme form
      // adds the item and the cart drawer auto-opens.
      const variantId =
        card?.dataset.customSelectedVariantId ||
        (fromCartRecommendations ? card?.querySelector(S.variantInput)?.value : '');
      if (!variantId) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      // log('intercepting cart-add', { variantId });

      const quantity = parseInt(card.querySelector(S.qtyInput)?.value, 10) || 1;

      // Pending button state
      const prevAriaBusy = addBtn.getAttribute('aria-busy');
      addBtn.disabled = true;
      addBtn.setAttribute('aria-busy', 'true');

      const restoreBtn = () => {
        addBtn.disabled = false;
        prevAriaBusy ? addBtn.setAttribute('aria-busy', prevAriaBusy) : addBtn.removeAttribute('aria-busy');
      };

      // Collect cart-drawer section IDs so Shopify re-renders them.
      const sectionIds = [
        ...new Set(
          [...document.querySelectorAll(S.cartSections)]
            .map((el) => el.dataset.sectionId)
            .filter(Boolean)
        ),
      ];

      // On the cart page, add via an UNPATCHED fetch so ReCharge never sees the
      // request and its drawer never opens (no flash). Elsewhere, use the normal
      // (patched) fetch so ReCharge opens its drawer as usual.
      const onCartPage = isOnCartPage();
      const doFetch = onCartPage ? getUnpatchedFetch() : window.fetch.bind(window);


      try {
        const res = await doFetch(CART_ADD_URL, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity,
            ...(sectionIds.length && { sections: sectionIds.join(',') }),
          }),
        });

        if (!res.ok) throw await res.json();

        const data = await res.json();
        // log('added to cart', data);

        restoreBtn();

        // Trigger theme's "added" pulse animation
        addBtn.dataset.added = 'true';
        setTimeout(() => addBtn.removeAttribute('data-added'), 1500);

        // Recommendations widget listens for this to re-seed from the new first line.
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { sections: data.sections, source: 'custom-variant-pills' } },
          })
        );

        // Update visible item-count badges, then morph the cart page in place.
        const cart = await doFetch(CART_GET_URL).then((r) => r.json());
        document.querySelectorAll(S.cartCount).forEach((el) => {
          el.textContent = String(cart.item_count);
        });
        document.dispatchEvent(
          new CustomEvent('cart:item-count', { detail: { count: cart.item_count } })
        );

        if (onCartPage) {
          // Refresh line items without the drawer's auto-open, then bring the
          // updated bag into view.
          refreshCartInPlace({
            sections: data.sections,
            items: cart.items,
            itemCount: cart.item_count,
          });
          if (fromCartRecommendations) scrollCartPageToTop();
        } else {
          ['cart:refresh', 'cart:updated', 'on:cart:add'].forEach((name) => {
            document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: data }));
          });
          const drawer = document.querySelector(S.cartDrawer);
          if (typeof drawer?.open === 'function') drawer.open();
        }
      } catch (err) {
        console.error('[variant-pills] cart-add failed:', err);
        restoreBtn();
        if (err?.description) alert(err.description);
      }
    },
    true /* capture phase */
  );

  // Theme add-to-cart (when this script does not intercept) still emits
  // shopify:cart:lines-update with action "add". On the cart page the drawer
  // stays closed; scroll to the bag instead.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action === 'update') return;
    if (!isOnCartPage()) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('product-recommendations')) return;
    event.promise
      ?.then((result) => {
        if (result?.detail?.didError) return;
        scrollCartPageToTop();
      })
      .catch(() => {});
  });
})();















